import { supabase } from '../lib/supabase';

export const paymentHistoryService = {
  /**
   * Fetch all negotiations with their associated payments (receivables)
   */
  async getAllNegotiationsWithPayments() {
    const { data, error } = await supabase?.from('negotiations')?.select(`
        id,
        title,
        destination,
        sale_value,
        payment_status,
        sale_date,
        client_id,
        clients(name),
        receivables(
          id,
          amount,
          due_date,
          received_at,
          status,
          operator,
          payment_method,
          created_at
        )
      `)?.order('sale_date', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(negotiation => ({
      id: negotiation?.id,
      title: negotiation?.title,
      destination: negotiation?.destination,
      saleValue: negotiation?.sale_value,
      paymentStatus: negotiation?.payment_status,
      saleDate: negotiation?.sale_date,
      clientId: negotiation?.client_id,
      clientName: negotiation?.clients?.name || 'Cliente Desconhecido',
      receivables: (negotiation?.receivables || [])?.map(r => ({
          id: r?.id,
          amount: r?.amount,
          dueDate: r?.due_date,
          receivedAt: r?.received_at,
          status: r?.status,
          operator: r?.operator,
          paymentMethod: r?.payment_method,
          createdAt: r?.created_at
        }))?.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
    }));
  },

  /**
   * Update payment status
   */
  async updatePaymentStatus(receivableId, status, receivedAt = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData = {
      status,
      updated_at: new Date()?.toISOString()
    };

    if (receivedAt) {
      updateData.received_at = receivedAt;
    }

    const { data, error } = await supabase?.from('receivables')?.update(updateData)?.eq('id', receivableId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      status: data?.status,
      receivedAt: data?.received_at
    };
  },

  /**
   * Get payment statistics
   */
  async getPaymentStatistics() {
    const { data, error } = await supabase?.from('receivables')?.select('amount, status, due_date, received_at');

    if (error) throw error;

    const stats = {
      totalPaid: 0,
      totalOutstanding: 0,
      totalOverdue: 0,
      averagePaymentInterval: 0,
      nextPaymentDue: null
    };

    const paidPayments = [];
    const now = new Date();

    data?.forEach(payment => {
      const amount = parseFloat(payment?.amount);
      
      if (payment?.status === 'paid') {
        stats.totalPaid += amount;
        if (payment?.due_date && payment?.received_at) {
          const dueDate = new Date(payment.due_date);
          const receivedDate = new Date(payment.received_at);
          const interval = Math.abs(receivedDate - dueDate) / (1000 * 60 * 60 * 24);
          paidPayments?.push(interval);
        }
      } else {
        stats.totalOutstanding += amount;
        const dueDate = new Date(payment.due_date);
        
        if (dueDate < now) {
          stats.totalOverdue += amount;
        }
        
        if (!stats?.nextPaymentDue || dueDate < new Date(stats.nextPaymentDue)) {
          stats.nextPaymentDue = payment?.due_date;
        }
      }
    });

    if (paidPayments?.length > 0) {
      stats.averagePaymentInterval = Math.round(
        paidPayments?.reduce((a, b) => a + b, 0) / paidPayments?.length
      );
    }

    return stats;
  }
};